import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
	getNextPosId,
	getPosition,
	getUser,
	getUserLastPositionId,
	recordDeposit,
	RecordDepositRequest,
} from './client';

export const queryKeys = {
	user: (userId: string, baseUrl?: string) =>
		['user', userId, baseUrl || 'default'] as const,
	userLastPositionId: (userId: string, baseUrl?: string) =>
		['userLastPositionId', userId, baseUrl || 'default'] as const,
	nextPosId: (baseUrl?: string) => ['nextPosId', baseUrl || 'default'] as const,
	position: (posId: number, baseUrl?: string) =>
		['position', posId, baseUrl || 'default'] as const,
};

export function useUser(
	userId: string | null,
	enabled = true,
	baseUrl?: string,
) {
	return useQuery({
		queryKey: queryKeys.user(userId || '', baseUrl),
		queryFn: () => getUser(userId!, baseUrl),
		enabled: enabled && !!userId,
		staleTime: 5 * 60 * 1000,
		retry: 2,
	});
}

export function useUserLastPositionId(
	userId: string | null,
	enabled = true,
	baseUrl?: string,
) {
	return useQuery({
		queryKey: queryKeys.userLastPositionId(userId || '', baseUrl),
		queryFn: () => getUserLastPositionId(userId!, baseUrl),
		enabled: enabled && !!userId,
		retry: 2,
	});
}

export function useRecordDeposit(baseUrl?: string) {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (request: RecordDepositRequest) =>
			recordDeposit(request, baseUrl),
		onSuccess: (_, variables) => {
			queryClient.invalidateQueries({
				queryKey: queryKeys.user(variables.userId, baseUrl),
			});
			queryClient.invalidateQueries({
				queryKey: queryKeys.userLastPositionId(
					variables.userId,
					baseUrl,
				),
			});
		},
	});
}

export function useNextPosId(baseUrl?: string) {
	return useQuery({
		queryKey: queryKeys.nextPosId(baseUrl),
		queryFn: () => getNextPosId(baseUrl),
		staleTime: 1000, // 1 second - needs to be fresh before deposits
		refetchOnMount: 'always',
	});
}

export function usePosition(
	posId: number | null,
	enabled = true,
	baseUrl?: string,
) {
	return useQuery({
		queryKey: queryKeys.position(posId || 0, baseUrl),
		queryFn: () => getPosition(posId!, baseUrl),
		enabled: enabled && posId !== null && posId > 0,
		staleTime: 10 * 1000, // 10 seconds
		retry: 2,
	});
}

/**
 * Fetch multiple positions at once
 */
export function usePositions(posIds: number[], baseUrl?: string) {
	return useQuery({
		queryKey: ['positions', posIds.join(','), baseUrl || 'default'],
		queryFn: async () => {
			const results = await Promise.allSettled(
				posIds.map((id) => getPosition(id, baseUrl)),
			);

			return results
				.map((result, index) => {
					if (result.status === 'fulfilled') {
						return result.value;
					}
					// console.warn(`Failed to fetch position ${posIds[index]}`);
					return null;
				})
				.filter((p) => p !== null && p.result !== null);
		},
		enabled: posIds.length > 0,
		staleTime: 30 * 1000, // 30 seconds - reduce API calls
		refetchInterval: 30 * 1000, // Refetch every 30 seconds for updates
		refetchOnWindowFocus: false, // Don't refetch on window focus
		refetchOnMount: false, // Don't refetch on mount if data is fresh
		retry: 1, // Reduce retries to avoid rate limits
	});
}
